import { Datepicker } from "baseui/datepicker";
import React, { FC, useRef, useState } from "react";
import { LuCalendar } from "react-icons/lu";

import { parseTypedDate, sameDay } from "./typedDate";

type DatePickerSize = "sm" | "md" | "lg";

const DATE_PICKER_SIZE_CONFIG: Record<
  DatePickerSize,
  { height: string; inputHeight: string; fontSize: string }
> = {
  sm: { height: "32px", inputHeight: "30px", fontSize: "14px" },
  md: { height: "36px", inputHeight: "34px", fontSize: "14px" },
  lg: { height: "40px", inputHeight: "38px", fontSize: "16px" },
};

type Props = {
  value: Date | null;
  minDate?: Date;
  maxDate?: Date;
  onChange: (date: Date) => void;
  /** Called when the box is emptied (optional; without it clearing is ignored). */
  onClear?: () => void;
  /** Text shown while empty (default: the date format, e.g. YYYY/MM/DD). */
  placeholder?: string;
  mountNode?: string;
  styleType?: "default" | "ghost";
  fontSize?: string;
  fontStyle?: string;
  fontWeight?: string;
  color?: string;
  format?: string;
  mask?: string | null;
  width?: string;
  textAlign?: string;
  size?: DatePickerSize;
  popoverZIndex?: number;
  showCalendarIcon?: boolean;
  /**
   * Also accept a date typed in another common form (2027/01/31, Jan 31 2027,
   * ...) when the box loses focus. baseui only reads `format`, so other typed
   * dates showed in the box but were never saved (QA A6). Opt-in.
   */
  acceptTypedDates?: boolean;
  /**
   * A date typed before minDate / after maxDate (QA batch 14: it was ignored without a word
   * and the box kept showing it). The box goes back to the saved date; show the reason here.
   */
  onOutOfRange?: (date: Date, limit: "min" | "max") => void;
  /**
   * With acceptTypedDates: called on blur / Enter when the typed text isn't a
   * date ("invalid") or is outside minDate / maxDate ("out-of-range"), so the
   * window can say so (QA batch 15). Without it such text is ignored as before.
   */
  onInvalidTyped?: (reason: "invalid" | "out-of-range") => void;
};

export const StyledDatePicker: FC<Props> = ({
  value,
  onChange,
  onClear,
  placeholder,
  mountNode,
  minDate,
  maxDate,
  styleType = "default",
  fontSize,
  fontStyle,
  fontWeight,
  color,
  format,
  mask,
  width,
  textAlign,
  size = "md",
  popoverZIndex = 9999,
  showCalendarIcon = false,
  acceptTypedDates = false,
  onOutOfRange,
  onInvalidTyped,
}) => {
  // Bumped to redraw the box with the saved date after a refused typed date.
  const [resetKey, setResetKey] = useState(0);
  const mountElement = mountNode ? document.getElementById(mountNode) : null;
  const sizeConfig = DATE_PICKER_SIZE_CONFIG[size];
  // baseui's Datepicker is a class component; its close() shuts the calendar.
  const pickerRef = useRef<Datepicker | null>(null);

  const datePicker = (
    <Datepicker
      key={resetKey}
      ref={pickerRef}
      value={value}
      minDate={minDate || null}
      maxDate={maxDate || null}
      formatString={format}
      mask={mask}
      placeholder={placeholder}
      overrides={{
        Input: {
          props: {
            overrides: {
              Input: {
                props: {
                  mountNode: mountElement,
                },
                style: () =>
                  styleType === "ghost"
                    ? {
                        backgroundColor: "transparent",
                        paddingBottom: "0",
                        paddingTop: "0",
                        paddingRight: "0",
                        paddingLeft: "0",
                        fontSize: fontSize ?? "14px",
                        fontWeight: fontWeight ? fontWeight : "500",
                        width: width ? width : "100px",
                        cursor: "pointer",
                        fontStyle: fontStyle ? fontStyle : "base",
                        color: color ? color : "black",
                        textAlign: textAlign ? textAlign : "start",
                      }
                    : {
                        backgroundColor: "#FFFFFF",
                        fontSize: fontSize ?? sizeConfig.fontSize,
                        fontWeight: fontWeight ? fontWeight : "400",
                        height: sizeConfig.inputHeight,
                        paddingBottom: "0",
                        paddingTop: "0",
                        paddingLeft: "12px",
                        paddingRight: showCalendarIcon ? "44px" : "12px",
                      },
              },
              // baseui paints the box behind the text grey (theme input
              // fill). The text is narrower than the box in the ghost style
              // (invoice dates), so a grey rounded shape showed next to the
              // date (Roadmap 1854): the box and its container are white /
              // transparent now.
              InputContainer: {
                style: () => ({
                  backgroundColor:
                    styleType === "ghost" ? "transparent" : "#FFFFFF",
                }),
              },
              Root: {
                style: () =>
                  styleType === "ghost"
                    ? {
                        backgroundColor: "transparent",
                        borderBottomColor: "transparent",
                        borderTopColor: "transparent",
                        borderRightColor: "transparent",
                        borderLeftColor: "transparent",
                        borderBottomWidth: "0px",
                        borderTopWidth: "0px",
                        borderRightWidth: "0px",
                        borderLeftWidth: "0px",
                      }
                    : {
                        backgroundColor: "#FFFFFF",
                        height: sizeConfig.height,
                        borderTopWidth: "1px",
                        borderRightWidth: "1px",
                        borderBottomWidth: "1px",
                        borderLeftWidth: "1px",
                        borderTopColor: "#D4D6D9",
                        borderRightColor: "#D4D6D9",
                        borderBottomColor: "#D4D6D9",
                        borderLeftColor: "#D4D6D9",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                        borderBottomLeftRadius: "8px",
                      },
              },
            },
          },
        },
        MonthYearSelectPopover: {
          props: {
            mountNode: mountElement,
            overrides: {
              Body: {
                style: { zIndex: popoverZIndex },
              },
            },
          },
        },
        Popover: {
          props: {
            mountNode: mountElement,
            overrides: {
              Body: {
                style: {
                  zIndex: popoverZIndex,
                },
              },
            },
          },
        },
      }}
      onChange={({ date }) => {
        if (date instanceof Date) {
          onChange(date);
        } else if (!date && onClear) {
          onClear();
        }
      }}
    />
  );

  // The calendar's limits apply to typed dates too.
  // rangeOnly: only report a refused date (blur without acceptTypedDates).
  const commitTyped = (input: HTMLInputElement, rangeOnly = false) => {
    const typed = parseTypedDate(input.value, format);
    if (!typed) {
      if (rangeOnly) return;
      if (input.value.trim()) onInvalidTyped?.("invalid");
      else onClear?.();
      return;
    }
    if (sameDay(typed, value)) return;
    const day = new Date(
      typed.getFullYear(),
      typed.getMonth(),
      typed.getDate(),
    );
    const startOf = (date: Date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const limit =
      minDate && day < startOf(minDate)
        ? "min"
        : maxDate && day > startOf(maxDate)
        ? "max"
        : null;
    if (limit) {
      onInvalidTyped?.("out-of-range");
      if (onOutOfRange) {
        onOutOfRange(typed, limit);
        setResetKey((key) => key + 1);
      }
      return;
    }
    if (rangeOnly) return;
    onChange(typed);
  };

  const readTypedDate = (event: React.FocusEvent<HTMLElement>) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (acceptTypedDates) commitTyped(event.target);
    else if (onOutOfRange) commitTyped(event.target, true);
  };

  // Enter in a date box (QA batch 8): keep the typed date and close the
  // calendar, but never submit the form around it (New work Due date created
  // the work with today's date).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement))
      return;
    event.preventDefault();
    commitTyped(event.target);
    // The calendar stayed open after Enter (QA batch 11): close it.
    (pickerRef.current as unknown as { close?: () => void } | null)?.close?.();
  };

  if (!showCalendarIcon) {
    return (
      <div
        className={"contents"}
        onKeyDownCapture={handleKeyDown}
        onBlurCapture={readTypedDate}>
        {datePicker}
      </div>
    );
  }

  return (
    <div
      className={"relative w-full"}
      onKeyDownCapture={handleKeyDown}
      onBlurCapture={readTypedDate}>
      {datePicker}
      <div
        className={
          "pointer-events-none absolute right-0 top-1/2 z-[1] flex h-6 w-8 -translate-y-1/2 items-center justify-center border-l border-gray-300"
        }>
        <LuCalendar aria-hidden className={"h-4 w-4 text-[#7A8694]"} />
      </div>
    </div>
  );
};

type CompactDatePickerProps = {
  value: Date | null;
  onChange: (date: Date) => void;
  format: string;
  mountNodeId: string;
};

export const CompactDatePicker: FC<CompactDatePickerProps> = ({
  value,
  onChange,
  format,
  mountNodeId,
}) => {
  return (
    <div className={"relative"}>
      <Datepicker
        value={value}
        formatString={format}
        overrides={{
          MonthYearSelectPopover: {
            props: {
              overrides: {
                Body: {
                  style: { zIndex: 10000 },
                },
              },
            },
          },
          Input: {
            props: {
              mountNode: document.getElementById(mountNodeId),
              overrides: {
                Input: {
                  style: () => ({
                    height: "32px",
                    backgroundColor: "#FFFFFF",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#253747",
                    paddingRight: "40px",
                  }),
                },
                InputContainer: {
                  style: () => ({ backgroundColor: "#FFFFFF" }),
                },
                Root: {
                  style: () => ({
                    backgroundColor: "#FFFFFF",
                    height: "32px",
                    borderTopWidth: "1px",
                    borderRightWidth: "1px",
                    borderBottomWidth: "1px",
                    borderLeftWidth: "1px",
                    borderTopColor: "#D4D6D9",
                    borderRightColor: "#D4D6D9",
                    borderBottomColor: "#D4D6D9",
                    borderLeftColor: "#D4D6D9",
                  }),
                },
              },
            },
          },
          Popover: {
            props: {
              overrides: {
                Body: {
                  style: {
                    zIndex: 9999,
                  },
                },
              },
            },
          },
        }}
        onChange={({ date }) => {
          if (date instanceof Date) {
            onChange(date);
          }
        }}
      />
      <LuCalendar
        aria-hidden
        className={
          "pointer-events-none absolute right-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-[#C8D2DE]"
        }
      />
    </div>
  );
};
